import is from '@sindresorhus/is';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { JobLevel } from '~/shared/data-contract/job-level';
import type { JobRole } from '~/shared/data-contract/job-role';

import type { ApplicationFetchers } from '../../fetchers/create-application-fetchers';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

const createArchivedRolesQueryFn =
    (fetchArchivedJobRoles: ApplicationFetchers['fetchArchivedJobRoles']) =>
    (jobArchitectureId: string): (() => Promise<readonly JobRole[]>) => {
        return async () => {
            const result = await fetchArchivedJobRoles({
                includeProfiles: false,
                jobArchitectureId,
            });

            if (result.isOk === true) {
                return result.value;
            }

            throw result.error;
        };
    };

const createArchivedLevelsQueryFn =
    (fetchArchivedJobLevels: ApplicationFetchers['fetchArchivedJobLevels']) =>
    (jobArchitectureId: string): (() => Promise<readonly JobLevel[]>) => {
        return async () => {
            const result = await fetchArchivedJobLevels({
                includeProfiles: false,
                jobArchitectureId,
            });

            if (result.isOk === true) {
                return result.value;
            }

            throw result.error;
        };
    };

type UseArchivedItemsOptions = {
    readonly enabled?: boolean;
};

export type ArchivedItemsData = {
    readonly archivedLevels: readonly JobLevel[];
    readonly archivedRoles: readonly JobRole[];
};

export const useArchivedRoles = (
    jobArchitectureId: string,
    options?: UseArchivedItemsOptions,
): UseQueryResult<readonly JobRole[], Error> => {
    const enabled = options?.enabled ?? true;
    const { fetchArchivedJobRoles } = useApplicationFetchers();

    return useQuery({
        enabled,
        queryFn: createArchivedRolesQueryFn(fetchArchivedJobRoles)(jobArchitectureId),
        queryKey: jobArchitectureKeys.archivedRoles(jobArchitectureId),
        staleTime: 10_000,
        throwOnError: false,
    });
};

export const useArchivedLevels = (
    jobArchitectureId: string,
    options?: UseArchivedItemsOptions,
): UseQueryResult<readonly JobLevel[], Error> => {
    const enabled = options?.enabled ?? true;
    const { fetchArchivedJobLevels } = useApplicationFetchers();

    return useQuery({
        enabled,
        queryFn: createArchivedLevelsQueryFn(fetchArchivedJobLevels)(jobArchitectureId),
        queryKey: jobArchitectureKeys.archivedLevels(jobArchitectureId),
        staleTime: 10_000,
        throwOnError: false,
    });
};

type UseArchivedItemsResult = {
    readonly data: ArchivedItemsData | undefined;
    readonly error: Error | null;
    readonly isError: boolean;
    readonly isLoading: boolean;
    readonly isPending: boolean;
};

export const useArchivedItems = (
    jobArchitectureId: string,
    options?: UseArchivedItemsOptions,
): UseArchivedItemsResult => {
    const archivedRolesQuery = useArchivedRoles(jobArchitectureId, options);
    const archivedLevelsQuery = useArchivedLevels(jobArchitectureId, options);

    const isLoading = archivedRolesQuery.isLoading || archivedLevelsQuery.isLoading;
    const isPending = archivedRolesQuery.isPending || archivedLevelsQuery.isPending;
    const isError = archivedRolesQuery.isError || archivedLevelsQuery.isError;
    const error = archivedRolesQuery.error ?? archivedLevelsQuery.error;

    const data =
        !is.nullOrUndefined(archivedRolesQuery.data) && !is.nullOrUndefined(archivedLevelsQuery.data)
            ? {
                  archivedLevels: archivedLevelsQuery.data,
                  archivedRoles: archivedRolesQuery.data,
              }
            : undefined;

    return {
        data,
        error,
        isError,
        isLoading,
        isPending,
    };
};
